import { DataGridSidePanelId, TableFilterState, TableSortingState } from '@ibsys/kit';
import type {
  CustomChartDefinition,
  DashboardPreset,
  DashboardQualitySettings,
} from '@/entities/dashboards/model/api/types';
import type { PhraseSearchRole } from '@/entities/search/model/api/conversation/types';

/** Код языка интерфейса */
export type Languages = 'en' | 'ru';

/** Код темы оформления из project-config (например 'light', 'dark' или кастомный код) */
export type Theme = string;

/** Допустимые режимы отображения страницы диалогов */
export const DIALOGS_LAYOUTS = ['classic', 'one-page', 'visualizer'] as const;

/** Режим отображения страницы диалогов */
export type DialogsLayout = (typeof DIALOGS_LAYOUTS)[number];

/** Стандартные колонки экспертного заключения, отображаемые в таблице диалогов */
export type ExpertReportSettingsColumn = 'id' | 'rating' | 'startedAt' | 'finishedAt' | 'status';

/**
 * Настройка отображения колонок одного экспертного заключения в таблице диалогов
 * (spec: frontend/conversation/table-column-configuration.md, api/search/query.md).
 */
export type ExpertReportSettings = {
  /** ID экспертного отчёта (`expert_report.id`) */
  id: number;
  /** Стандартные колонки, выбранные для отображения */
  columns: ExpertReportSettingsColumn[];
  /** ID блоков отчёта, для которых отображается колонка с баллом */
  blockIds: number[];
};

/** Пользовательские настройки страницы диалогов */
export type DialogsSettings = {
  /** Коды атрибутов, отображаемых как дополнительные колонки таблицы */
  attributes: string[];
  /** Коды основных колонок таблицы диалогов */
  mainColumns: string[];
  /** Настройки колонок по экспертным отчётам (стандартные поля и блоки) */
  expertReport?: ExpertReportSettings[];
  /** Активный режим отображения страницы диалогов */
  layout: DialogsLayout;
  /**
   * Ключи скрытых табов секции данных диалога.
   * Для фиксированных табов — статичные ключи (`attributes`, `comments`,
   * `attachments`, `raw-data`); для экспертных отчётов — `expert-report-<id>`.
   */
  hiddenDataTabs?: string[];
  /**
   * Последний выбранный пользователем таб секции данных диалога — ключи те же, что в
   * {@link DialogsSettings.hiddenDataTabs}. Применяется при открытии диалога, если таб не задан
   * адресом. Записи нет — пользователь таб не выбирал, диалог открывается на табе атрибутов.
   */
  lastDataTab?: string;
  /**
   * Коды результата правил, отобранных к показу в заключении по диалогу.
   * Отбор один на все заключения всех диалогов. Записи нет или список пуст — отбор
   * пользователь не выставлял, показаны правила всех результатов.
   */
  expertReportRuleResults?: string[];
  /**
   * Идентификаторы свёрнутых блоков атрибутов в карточке диалога.
   * Ключ — узел иерархии атрибутов, он одинаков во всех диалогах, поэтому настройка
   * переносится между ними. Хранятся только свёрнутые блоки: блока нет в списке — он
   * развёрнут. Записи блоков, которых в открытом диалоге нет, сохраняются — они
   * применятся в диалогах, где такие блоки есть.
   */
  attrCollapsedNodes?: string[];
  /**
   * Пользовательский порядок верхнеуровневых блоков атрибутов в карточке диалога
   * (идентификаторы узлов верхнего уровня). Блоки, которых в списке нет, отображаются
   * после перечисленных — в системном порядке.
   */
  attrTopLevelOrder?: string[];
  /**
   * Меню навигации по атрибутам в карточке диалога свёрнуто.
   * Хранится отклонение от умолчания: записи нет — меню развёрнуто.
   */
  attrNavCollapsed?: boolean;
  /**
   * Ширина развёрнутого меню навигации по атрибутам в карточке диалога, в пикселях.
   * Ширину подбирают под длину наименований атрибутов проекта, поэтому она не относится
   * к конкретному диалогу и переносится между ними. Хранится отклонение от умолчания:
   * записи нет — меню открывается шириной, заданной системой.
   */
  attrNavWidth?: number;
};

/** Персистируемое состояние поисковой строки таблицы (хранится вместе с фильтрами и сортировкой) */
export type TableSearchState = {
  /** Текст поискового запроса */
  query: string;
  /** Режим поиска: естественный язык или DSL */
  mode: 'nl' | 'dsl';
};

/** Режим поиска по содержимому диалога — взаимоисключающие "по репликам" и "по комментариям" */
export type ContentSearchMode = 'phrase' | 'comment';

/**
 * Персистируемое состояние режима поиска по содержимому диалога (spec: 03 IL/Переключатели поиска
 * по содержимому диалога). Хранится в общем сторе настроек таблицы вместе с фильтрами и сортировкой —
 * отдельного хранилища под него нет.
 */
export type TableContentSearchState = {
  /** Активный режим поиска по содержимому */
  mode: ContentSearchMode;
  /** Подтверждённое (committed) значение запроса; пустая строка эквивалентна отсутствию запроса */
  value: string;
  /**
   * Признак того, что в текущем сеансе поиска (фиксированные `mode` + `value`) пользователь
   * явно выбрал сортировку, отклонившись от дефолтной релевантности. Пока `false`/отсутствует —
   * при активном поиске действует дефолт `relevance DESC`. Хранится здесь (а не в эфемерном
   * состоянии секции), чтобы уход в карточку диалога и возврат на список не считались новой
   * активацией и не возвращали релевантность поверх выбора пользователя.
   */
  isSortingPicked?: boolean;
  /**
   * Выбранная сторона говорящего для поиска по репликам (`mode === 'phrase'`).
   * Отсутствие значения — «Все» (обе стороны); в запрос роль уходит только при непустом `value`.
   */
  role?: PhraseSearchRole;
  /**
   * Выбранные операторы для поиска по репликам (`mode === 'phrase'`).
   * Отсутствие/пустой массив — «Все» (все консультанты); ограничение уходит в запрос только при
   * непустом `value` **и** `role === 'employee'`. Выбор сохраняется при смене говорящего (не стирается).
   */
  phraseSearchEmployeeIds?: number[];
  /**
   * Выбранные авторы комментариев для поиска по комментариям (`mode === 'comment'`).
   * Отсутствие/пустой массив — «Все» (все авторы); ограничение уходит в запрос только при непустом `value`.
   */
  commentAuthorIds?: number[];
};

/**
 * Боковые панели управления таблицей DataGrid: список колонок и список фильтров.
 * Перечисление нужно и в рантайме — сохранённое значение проверяется по нему при загрузке настроек.
 */
export const TABLE_SIDE_PANELS = ['columns', 'filters'] as const satisfies readonly DataGridSidePanelId[];

/** Идентификатор боковой панели управления таблицей */
export type TableSidePanel = (typeof TABLE_SIDE_PANELS)[number];

/**
 * Направление текста заголовков колонок DataGrid.
 * Дублирует `DataGridHeaderOrientation` из `@ibsys/kit` — сам тип не экспортирован из
 * публичного барреля кита (только сущности, которые его используют, например `DataGridColumn`).
 */
export type TableHeaderOrientation = 'horizontal' | 'vertical';

/** Персистируемые настройки таблицы: фильтры, сортировка, пагинация, поиск и состояние колонок DataGrid */
export type TableSettings = {
  /** Активные фильтры таблицы */
  filters?: TableFilterState;
  /** Текущий offset пагинации */
  offset?: number;
  /** Активная сортировка таблицы */
  sorting?: TableSortingState;
  /** Состояние поисковой строки */
  search?: TableSearchState;
  /** Состояние поиска по содержимому диалога (режим + подтверждённый запрос) */
  contentSearch?: TableContentSearchState | null;
  /** Порядок листовых колонок DataGrid */
  columnOrder?: string[];
  /** Видимость колонок DataGrid; отсутствие ключа = видима, false = скрыта */
  columnVisibility?: Record<string, boolean>;
  /** Закреплённые колонки DataGrid */
  columnPinning?: { left?: string[]; right?: string[] };
  /**
   * Ширина колонок DataGrid в пикселях (id колонки → ширина).
   * Записи нет — колонка показывается в системной ширине, заданной её определением.
   * Ширина — такая же настройка вида таблицы, как набор и порядок колонок,
   * поэтому входит в снимок пресета ({@link TABLE_PRESET_SNAPSHOT_KEYS}).
   */
  columnSizing?: Record<string, number>;
  /**
   * Ширина панели управления колонками DataGrid в пикселях.
   * Ширину подбирают под длину наименований колонок таблицы, поэтому она относится к таблице
   * целиком, а не к конкретному виду — в снимок пресета ({@link TABLE_PRESET_SNAPSHOT_KEYS})
   * не входит. Записи нет — панель подбирает ширину под контент сама.
   */
  columnsPanelWidth?: number;
  /**
   * Раскрытые группы колонок на табе «Видимость» панели управления колонками.
   * Записи нет — все группы свёрнуты. Раскрытие — способ работы с панелью, а не вид таблицы,
   * поэтому в снимок пресета ({@link TABLE_PRESET_SNAPSHOT_KEYS}) не входит. Идентификаторы
   * исчезнувших групп сохраняются: состав групп зависит от данных (атрибуты, заключения),
   * и группа, пропавшая из одного состава, раскроется в том, где она есть.
   */
  columnsPanelExpandedGroups?: string[];
  /**
   * Открытая боковая панель управления таблицей (колонки или фильтры).
   * Записи нет — панели закрыты. Панель — часть раскладки таблицы, а не её вида,
   * поэтому в снимок пресета ({@link TABLE_PRESET_SNAPSHOT_KEYS}) не входит.
   */
  activeSidePanel?: TableSidePanel;
  /**
   * Боковая панель закреплена продолжением таблицы: грид ужимается на её ширину и панель не
   * закрывается кликом мимо. Хранится отклонение от умолчания: записи нет — панель плавающая
   * (выезжает поверх таблицы). Как и {@link TableSettings.activeSidePanel}, в снимок пресета не входит.
   */
  sidePanelPinned?: boolean;
  /**
   * Направление текста в заголовках колонок DataGrid (тумблер в шапке встроенной панели колонок).
   * Управляемое значение: `headerOrientation` + `onHeaderOrientationChange` на уровне `DataGrid`
   * (кит с 0.1.37). Вид таблицы, поэтому входит в снимок пресета ({@link TABLE_PRESET_SNAPSHOT_KEYS}).
   */
  headerOrientation?: TableHeaderOrientation;
  /** Индекс первой видимой строки (для восстановления позиции прокрутки) */
  scrollIndex?: number;
  /**
   * Ключ состава списка, для которого сохранён {@link TableSettings.scrollIndex}.
   * Позиция прокрутки — это место в конкретном составе строк: сменились фильтры, сортировка
   * или поиск — прежнего места в списке уже нет, и восстанавливать позицию нельзя.
   * Состав описывает потребитель, поэтому содержимое ключа для хранилища непрозрачно.
   */
  scrollKey?: string;
  /**
   * Ключ внешней выборки, для которой пользователь скрыл предложение сохранить её в пресет.
   * Хранится вместе с настройками таблицы, чтобы отклонение пережило перезагрузку страницы;
   * привязка к ключу означает, что другая выборка предлагается к сохранению заново.
   */
  dismissedSelectionKey?: string;
  /**
   * Описание активной внешней выборки для подсказки сохранения, привязанное к её ключу.
   * Приходит с дашборда в состоянии навигации, но состояние теряется при уходе в карточку диалога,
   * поэтому описание дублируется сюда — тогда подсказка не пропадает при выборе диалога в таблице
   * и переживает перезагрузку. `key` сверяется с составом текущей выборки: не совпал — описание неактуально.
   * `source` — источник выборки (авто-инсайт / зона качества); задаёт автоимя сохранённого пресета.
   */
  selectionDescription?: { key: string; text: string; source?: string };
  /**
   * Пользовательское имя пресета.
   * Заполнено **только** у записей-пресетов в `tables` (ключи вида `<tableName>:<uuid>`);
   * у активной рабочей записи (`tables[tableName]`) — отсутствует.
   */
  name?: string;
  /**
   * Источник внешней выборки, из которой создан пресет (авто-инсайт / зона качества).
   * Заполнено **только** у записей-пресетов, сохранённых из выборки; у пресетов, заведённых
   * пользователем вручную, — отсутствует. Нумерация одноимённых выборок считается по этому
   * полю, а не по префиксу имени: имя локализовано и при смене языка нумерация начиналась бы
   * заново.
   */
  selectionSource?: string;
  /**
   * Идентификатор применённого пресета.
   * Заполнено **только** у активной рабочей записи (`tables[tableName]`).
   * Соответствует ключу в `tables` (полный, с префиксом таблицы).
   */
  activePresetId?: string;
  /**
   * Пресет закреплён в левом меню отдельным пунктом.
   * Заполнено **только** у записей-пресетов; хранится отклонение от умолчания:
   * записи нет — пресет не закреплён.
   */
  pinnedToMenu?: boolean;
  /**
   * Отложенный личный вид таблицы (снимок полей {@link TABLE_PRESET_SNAPSHOT_KEYS}),
   * зафиксированный при входе в слой выборки/пресета. Снятие слоя возвращает таблицу к нему.
   * Заполнено **только** у активной рабочей записи (`tables[tableName]`), пока слой активен.
   */
  previousView?: TableViewSnapshot;
  /**
   * Пресет, действовавший до входа в слой внешней выборки (ключ в `tables`).
   * Выборка открывается поверх пресета и снимает его активность, поэтому возврат к виду пресета
   * возможен только по этой записи. Заполнено **только** у активной рабочей записи
   * (`tables[tableName]`), пока открыт слой выборки поверх пресета.
   */
  previousPresetId?: string;
};

/**
 * Снимок вида таблицы — те же поля, что входят в пресет ({@link TABLE_PRESET_SNAPSHOT_KEYS}).
 * Используется как отложенный личный вид (`previousView`) при работе со слоем выборки/пресета.
 */
export type TableViewSnapshot = Pick<TableSettings, TablePresetSnapshotKey>;

/**
 * Поля {@link TableSettings}, которые входят в снимок пресета.
 * Исключаются: `search`, `contentSearch`, `offset`, `scrollIndex`, `scrollKey`, `activePresetId` —
 * это позиция/состояние поиска, а не «настройка вида таблицы».
 */
export type TablePresetSnapshotKey =
  | 'filters'
  | 'sorting'
  | 'columnOrder'
  | 'columnVisibility'
  | 'columnPinning'
  | 'columnSizing'
  | 'headerOrientation';

/** Список полей TableSettings, которые сохраняются в пресет. */
export const TABLE_PRESET_SNAPSHOT_KEYS: readonly TablePresetSnapshotKey[] = [
  'filters',
  'sorting',
  'columnOrder',
  'columnVisibility',
  'columnPinning',
  'columnSizing',
  'headerOrientation',
] as const;

/** Допустимые панели боковой панели */
export const SIDEBAR_PANELS = ['copilot', 'navigation'] as const;

/** Идентификатор активной панели боковой панели */
export type SidebarPanels = (typeof SIDEBAR_PANELS)[number];

/** Допустимые режимы отображения копилота */
export const COPILOT_VIEWS = ['list', 'chat'] as const;

/** Режим отображения копилота в боковой панели */
export type CopilotView = (typeof COPILOT_VIEWS)[number];

/** Пользовательские настройки боковой панели */
export type SidebarSettings = {
  /** Активная панель: навигация или копилот */
  activePanel?: SidebarPanels;
  /** Режим отображения копилота: список чатов или активный чат */
  copilotView?: CopilotView;
  /** ID активного чата копилота; null — чат не выбран */
  copilotActiveChatId?: number | null;
  /** Ширина панели копилота в пикселях */
  copilotWidth?: number;
};

/** Пользовательские настройки дашборда качества СКТ — см. {@link DashboardQualitySettings} */
export type DashboardQualityUserSettings = DashboardQualitySettings;

/**
 * Пользовательские дашборды-пресеты страницы дашбордов.
 *
 * В отличие от пресетов таблицы, пресет здесь — не снимок текущего вида экрана, а самостоятельный
 * состав диаграмм: он заводится в конструкторе и текущим состоянием страницы не переписывается.
 */
export type DashboardPresetsSettings = {
  /** Пресеты по их идентификатору */
  presets?: Record<string, DashboardPreset>;
  /** Применённый пресет; записи нет — показан стандартный дашборд */
  activePresetId?: string;
};

/** Экшены стора настроек */
export type SettingStoreAction = {
  /** Устанавливает глобальный язык интерфейса и применяет его к i18n */
  setLanguage: (language: Languages) => void;
  /** Устанавливает тему глобально и применяет её к документу */
  setTheme: (theme: Theme) => void;
  /** Обновляет настройки страницы диалогов для конкретного пользователя */
  setDialogsSettings: (userId: string, settings: Partial<DialogsSettings>) => void;
  /** Обновляет настройки таблицы по имени для конкретного пользователя */
  setTableSettings: (userId: string, tableName: string, settings: Partial<TableSettings>) => void;
  /**
   * Создаёт новый именованный пресет настроек таблицы как снимок текущей рабочей записи
   * `tables[tableName]` (с фильтрацией по {@link TABLE_PRESET_SNAPSHOT_KEYS}).
   * Сразу делает созданный пресет активным.
   * @param selectionSource - Источник внешней выборки, если пресет сохраняется из неё.
   * @returns Полный идентификатор пресета (ключ в `tables`).
   */
  saveTablePreset: (userId: string, tableName: string, presetName: string, selectionSource?: string) => string;
  /**
   * Перезаписывает вид существующего пресета текущим снимком рабочей записи `tables[tableName]`.
   * Меняются только поля снимка ({@link TABLE_PRESET_SNAPSHOT_KEYS}); всё остальное описание
   * пресета — имя, источник выборки, закрепление в меню — сохраняется.
   * Если пресет не найден — операция игнорируется.
   */
  updateTablePreset: (userId: string, tableName: string, presetId: string) => void;
  /**
   * Применяет пресет к рабочей записи `tables[tableName]`: копирует поля снимка,
   * выставляет `activePresetId`. Поля поиска/позиции (`search`, `contentSearch`,
   * `offset`, `scrollIndex`, `scrollKey`) остаются в активной записи без изменений.
   *
   * При входе в слой из личного вида (таблица не была ни в пресете, ни в выборке) текущий вид
   * откладывается в `previousView` — снятие пресета вернёт таблицу к нему.
   */
  applyTablePreset: (userId: string, tableName: string, presetId: string) => void;
  /**
   * Входит в слой вида таблицы, не сохранённый как именованный пресет (внешняя выборка):
   * поля из `view` замещают одноимённые в рабочей записи, `activePresetId` снимается,
   * прочие поля снимка (например, колонки) остаются как есть. При `options.backup` и входе
   * в слой из личного вида (таблица не была ни в пресете, ни в выборке) текущий вид откладывается
   * в `previousView` для возврата при снятии слоя, а снятый пресет — в `previousPresetId`.
   * Повторный вход по той же или другой выборке отложенный вид и отложенный пресет не меняет.
   */
  enterTableView: (userId: string, tableName: string, view: TableViewSnapshot, options?: { backup?: boolean }) => void;
  /**
   * Удаляет пресет. Ссылки на него в рабочих записях сбрасываются: и активность
   * (`activePresetId`), и точка возврата слоя выборки (`previousPresetId`).
   */
  deleteTablePreset: (userId: string, presetId: string) => void;
  /** Переименовывает пресет. Если пресет не найден — операция игнорируется. */
  renameTablePreset: (userId: string, presetId: string, name: string) => void;
  /**
   * Закрепляет пресет в левом меню или снимает закрепление.
   * Если пресет не найден — операция игнорируется.
   */
  setTablePresetPinned: (userId: string, presetId: string, pinned: boolean) => void;
  /**
   * Снимает активный пресет рабочей записи таблицы. Если отложен личный вид (`previousView`) —
   * возвращает таблицу к нему и очищает отложенный вид; иначе просто сбрасывает `activePresetId`
   * без изменения настроек (обратная совместимость с пресетами без отложенного вида).
   * Без активного пресета не делает ничего: слой внешней выборки снимается `exitTableSelection`.
   */
  clearActiveTablePreset: (userId: string, tableName: string) => void;
  /**
   * Выходит из слоя внешней выборки: убирает выборку из отбора таблицы в любом состоянии.
   * Возвращает пресет, поверх которого выборка была открыта (`previousPresetId`), иначе —
   * отложенный личный вид (`previousView`), а если нет и его, просто вычёркивает выборку из
   * действующих фильтров. Фильтр выборки задаётся `selectionFilterId`: его же вычищает из
   * возвращаемого личного вида — в унаследованных состояниях выборка могла попасть и туда.
   */
  exitTableSelection: (userId: string, tableName: string, selectionFilterId: string) => void;
  /** Обновляет настройки боковой панели для конкретного пользователя */
  setSidebarSettings: (userId: string, settings: Partial<SidebarSettings>) => void;
  /** Обновляет настройки дашборда качества СКТ для конкретного пользователя */
  setDashboardQualitySettings: (userId: string, settings: Partial<DashboardQualitySettings>) => void;
  /**
   * Заводит новый пользовательский дашборд-пресет и сразу делает его активным.
   * @returns Идентификатор созданного пресета.
   */
  createDashboardPreset: (userId: string, name: string, charts: CustomChartDefinition[]) => string;
  /**
   * Меняет название и/или состав диаграмм пресета. Переданные поля замещаются целиком,
   * непереданные остаются как есть. Если пресет не найден — операция игнорируется.
   */
  updateDashboardPreset: (
    userId: string,
    presetId: string,
    changes: { name?: string; charts?: CustomChartDefinition[] },
  ) => void;
  /** Применяет пресет: страница показывает его диаграммы вместо стандартного дашборда. */
  applyDashboardPreset: (userId: string, presetId: string) => void;
  /** Снимает активный пресет — страница возвращается к стандартному дашборду. */
  clearActiveDashboardPreset: (userId: string) => void;
  /** Удаляет пресет; удаление активного возвращает страницу к стандартному дашборду. */
  deleteDashboardPreset: (userId: string, presetId: string) => void;
  /** Закрепляет пресет отдельным пунктом левого меню или снимает закрепление. */
  setDashboardPresetPinned: (userId: string, presetId: string, pinned: boolean) => void;
  /**
   * Инициализирует настройки пользователя из бэкенда.
   * Полностью заменяет текущие настройки пользователя данными с сервера,
   * дополняя отсутствующие поля значениями по умолчанию.
   */
  initUserSettings: (userId: string, settings: UiSettings) => void;
  /** Устанавливает глобальный URL бэкенда (переопределяет DEFAULT_API_HOST) */
  setApiHost: (apiHost: string) => void;
  /** Сбрасывает URL бэкенда к DEFAULT_API_HOST */
  resetApiHost: () => void;
  /** Устанавливает сервисный токен для авторизации запросов */
  setServiceToken: (token: string) => void;
  /** Удаляет сервисный токен */
  clearServiceToken: () => void;
};

/** Все пользовательские настройки, хранящиеся в разрезе одного пользователя */
export type SettingStoreUserValues = {
  /** Настройки страницы диалогов */
  dialogsPage: DialogsSettings;
  /** Настройки таблиц по имени таблицы */
  tables: Record<string, TableSettings>;
  /** Настройки боковой панели */
  sidebar: SidebarSettings;
  /** Настройки дашборда качества СКТ */
  dashboardQuality: DashboardQualitySettings;
  /** Пользовательские дашборды-пресеты и применённый из них */
  dashboardPresets: DashboardPresetsSettings;
};

/**
 * Тип пользовательских настроек интерфейса при обмене с бэкендом
 * (GET ProfileData.uiSettings / PUT /profile/ui-settings).
 *
 * Содержит только персональные настройки пользователя — без темы и языка,
 * которые являются глобальными и хранятся в localStorage независимо.
 * Секции и их поля опциональны: набор сохранён прежним составом настроек либо частично отброшен
 * как непонятный, а недостающее фронт дополняет значениями из {@link initialUserValues}.
 */
export type UiSettings = {
  /** Настройки страницы диалогов */
  dialogsPage?: Partial<DialogsSettings>;
  /** Настройки таблиц по имени таблицы */
  tables?: Record<string, TableSettings>;
  /** Настройки боковой панели */
  sidebar?: SidebarSettings;
  /** Настройки дашборда качества СКТ */
  dashboardQuality?: DashboardQualitySettings;
  /** Пользовательские дашборды-пресеты и применённый из них */
  dashboardPresets?: DashboardPresetsSettings;
};

/** Словарь пользовательских настроек: ключ — строковый userId */
export type SettingsMap = {
  [userId: string]: SettingStoreUserValues;
};

/** Глобальные поля стора настроек (не привязаны к пользователю) */
export type SettingsStoreValues = {
  /** Пользовательские настройки в разрезе userId */
  settings: SettingsMap;
  /** Тема оформления; задаётся глобально, так как может быть выбрана до авторизации */
  theme: Theme;
  /** URL бэкенда; при отсутствии используется DEFAULT_API_HOST */
  apiHost?: string;
  /** Язык интерфейса */
  language?: Languages;
  /** Сервисный токен для авторизации запросов */
  serviceToken?: string;
};

/** Полный тип стора настроек: объединяет экшены и глобальные поля */
export type SettingStore = SettingStoreAction & SettingsStoreValues;
